import React, { useState } from "react";
import styles from "./VehicleModelsPage.module.css";
import { VehicleModel } from "./types";
import { mockVehicleModels } from "./mockData";

export default function VehicleModelsPage() {
  const [vehicleModels, setVehicleModels] = useState<VehicleModel[]>(mockVehicleModels);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingModel, setEditingModel] = useState<VehicleModel | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Filter vehicle models based on search
  const filteredModels = vehicleModels.filter(model => 
    model.modelName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    model.bodyType.toLowerCase().includes(searchQuery.toLowerCase()) ||
    model.powertrain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setIsCreating(true);
    setEditingModel({
      id: "",
      modelName: "",
      bodyType: "Sedan",
      rangeOnFull: "",
      class: "A",
      packageCapacity: 0,
      passengerSeats: 0,
      autonomous: false,
      powertrain: "ICE",
      vehiclesInFleet: 0
    });
    setShowDrawer(true);
  };

  const handleEdit = (model: VehicleModel) => {
    setIsCreating(false);
    setEditingModel({ ...model });
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingModel(null);
    setIsCreating(false);
  };

  const handleSave = () => {
    if (!editingModel) return;

    if (isCreating) {
      // Add new model
      const newModel = {
        ...editingModel,
        id: Date.now().toString()
      };
      setVehicleModels([...vehicleModels, newModel]);
    } else {
      // Update existing model
      setVehicleModels(vehicleModels.map(m => 
        m.id === editingModel.id ? editingModel : m
      ));
    }

    handleCloseDrawer();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this vehicle model?")) {
      setVehicleModels(vehicleModels.filter(m => m.id !== id));
    }
  };

  const updateField = (field: keyof VehicleModel, value: any) => {
    if (editingModel) {
      setEditingModel({ ...editingModel, [field]: value });
    }
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Vehicle Models</h1>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.createBtn} onClick={handleCreate}>
            + Create
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by Name, Body type, Powertrain"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>MODEL NAME</th>
              <th>BODY TYPE</th>
              <th>RANGE ON FULL (km)</th>
              <th>CLASS</th>
              <th>PACKAGE CAPACITY</th>
              <th>PASSENGER SEATS</th>
              <th>AUTONOMOUS</th>
              <th>POWERTRAIN</th>
              <th>VEHICLES IN FLEET</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredModels.map((model) => (
              <tr key={model.id}>
                <td className={styles.modelName}>{model.modelName}</td>
                <td>{model.bodyType}</td>
                <td>{model.rangeOnFull}</td>
                <td>{model.class}</td>
                <td>{model.packageCapacity}</td>
                <td>{model.passengerSeats}</td>
                <td>{model.autonomous ? "Yes" : "No"}</td>
                <td>{model.powertrain}</td>
                <td>{model.vehiclesInFleet}</td>
                <td>
                  <div className={styles.actions}>
                    <button 
                      className={styles.actionBtn} 
                      title="Edit"
                      onClick={() => handleEdit(model)}
                    >
                      Edit
                    </button>
                    <button 
                      className={styles.actionBtn} 
                      title="Delete"
                      onClick={() => handleDelete(model.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Results count */}
      <div className={styles.footer}>
        <span className={styles.resultsCount}>
          Showing {filteredModels.length} of {vehicleModels.length} models
        </span>
      </div>

      {/* Drawer Overlay */}
      {showDrawer && (
        <>
          <div className={styles.drawerOverlay} onClick={handleCloseDrawer} />
          <div className={styles.drawer}>
            {/* Drawer Header */}
            <div className={styles.drawerHeader}>
              <h2 className={styles.drawerTitle}>
                {isCreating ? "Add Vehicle Model" : "Edit Vehicle Model"}
              </h2>
              <button className={styles.closeBtn} onClick={handleCloseDrawer}>
                ✕
              </button>
            </div>

            {/* Drawer Content */}
            <div className={styles.drawerContent}>
              {editingModel && (
                <form className={styles.form}>
                  {/* Model Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Model Name</label>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Example: Honda Civic EX"
                      value={editingModel.modelName}
                      onChange={(e) => updateField("modelName", e.target.value)}
                    />
                  </div>

                  {/* Body Type & Class */}
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Body Type</label>
                      <select
                        className={styles.select}
                        value={editingModel.bodyType}
                        onChange={(e) => updateField("bodyType", e.target.value)}
                      >
                        <option value="Sedan">Sedan</option>
                        <option value="SUV">SUV</option>
                        <option value="Minivan">Minivan</option>
                        <option value="Hatchback">Hatchback</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.label}>Class</label>
                      <select
                        className={styles.select}
                        value={editingModel.class}
                        onChange={(e) => updateField("class", e.target.value)}
                      >
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </div>
                  </div>

                  {/* Number of Seats & Package Capacity */}
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Number Of Seats <span className={styles.info}>ⓘ</span>
                      </label>
                      <input
                        type="number"
                        className={styles.input}
                        value={editingModel.passengerSeats}
                        onChange={(e) => updateField("passengerSeats", parseInt(e.target.value) || 0)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Package Capacity <span className={styles.info}>ⓘ</span>
                      </label>
                      <input
                        type="number"
                        className={styles.input}
                        value={editingModel.packageCapacity}
                        onChange={(e) => updateField("packageCapacity", parseInt(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  {/* Range on Full */}
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Range On Full <span className={styles.info}>ⓘ</span>
                    </label>
                    <div className={styles.inputWithUnit}>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="500"
                        value={editingModel.rangeOnFull.replace(' km', '')}
                        onChange={(e) => updateField("rangeOnFull", e.target.value + " km")}
                      />
                      <span className={styles.unit}>km</span>
                    </div>
                  </div>

                  {/* Powertrain */}
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Powertrain</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="powertrain"
                          value="ICE"
                          checked={editingModel.powertrain === "ICE"}
                          onChange={(e) => updateField("powertrain", e.target.value)}
                        />
                        <span>Internal combustion engine (ICE)</span>
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="powertrain"
                          value="EV"
                          checked={editingModel.powertrain === "EV"}
                          onChange={(e) => updateField("powertrain", e.target.value)}
                        />
                        <span>Electric</span>
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="powertrain"
                          value="Hybrid"
                          checked={editingModel.powertrain === "Hybrid"}
                          onChange={(e) => updateField("powertrain", e.target.value)}
                        />
                        <span>Hybrid</span>
                      </label>
                    </div>
                  </div>

                  {/* Drive Mode */}
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Drive mode</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="autonomous"
                          checked={!editingModel.autonomous}
                          onChange={() => updateField("autonomous", false)}
                        />
                        <span>Traditional (human driver)</span>
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="autonomous"
                          checked={editingModel.autonomous}
                          onChange={() => updateField("autonomous", true)}
                        />
                        <span>Autonomous</span>
                      </label>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Drawer Footer */}
            <div className={styles.drawerFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={handleSave}>
                {isCreating ? "Add Vehicle Model" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}